//@ts-nocheck
import { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { useFinanceTypes, usePhases } from "@/state/cacheStoreHooks";
import { useGameStore } from "@/state/gameStore";
import { analyzePhase } from "@/components/dev/BalancingDashboard/balancing.utils";
import type { RoomVariant } from "@/types/game";

type StatsAmount = "min" | "max" | "avg";

type Props = {
    onClose?: () => void;
};

const STORAGE_KEY = "quickPlayToolFormData";

type FormData = {
    useMessageDelay: boolean;
    roomVariant: RoomVariant;
    selectedPhaseId: string | null;
    selectedFinanceTypeId: string | null;
    cashOverride: number;
    investmentsOverride: number;
    happinessOverride: number;
    messageLimit: number;
};

const defaultFormData: FormData = {
    useMessageDelay: false,
    roomVariant: "a",
    selectedPhaseId: null,
    selectedFinanceTypeId: null,
    cashOverride: 0,
    investmentsOverride: 0,
    happinessOverride: 5,
    messageLimit: 0,
};

const loadFormData = (fallbackPhaseId: string | null): FormData => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...defaultFormData, selectedPhaseId: fallbackPhaseId };
        return { ...defaultFormData, selectedPhaseId: fallbackPhaseId, ...JSON.parse(raw) };
    } catch {
        return { ...defaultFormData, selectedPhaseId: fallbackPhaseId };
    }
};

const saveFormData = (data: FormData) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
        // silently fail
    }
};

const QuickPlayTool = ({ onClose }: Props) => {
    const phases = usePhases();
    const financeTypes = useFinanceTypes();

    const fallbackPhaseId = phases[0]?.documentId ?? null;
    const [formData, setFormData] = useState<FormData>(() => loadFormData(fallbackPhaseId));

    const {
        useMessageDelay,
        roomVariant,
        selectedPhaseId,
        selectedFinanceTypeId,
        cashOverride,
        investmentsOverride,
        happinessOverride,
        messageLimit,
    } = formData;

    const update = useCallback(<K extends keyof FormData>(key: K, value: FormData[K]) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleReset = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setFormData({ ...defaultFormData, selectedPhaseId: fallbackPhaseId });
    }, [fallbackPhaseId]);

    const selectedPhase = useMemo(
        () => phases.find((p) => p.documentId === selectedPhaseId) ?? null,
        [phases, selectedPhaseId],
    );

    const computeDeltas = useCallback(
        (amount: StatsAmount) => {
            const selectedIndex = phases.findIndex((p) => p.documentId === selectedPhaseId);
            if (selectedIndex <= 0) return { cash: 0, investments: 0, happiness: 0 };

            const previousPhases = phases.slice(0, selectedIndex);
            const previousAnalyses = previousPhases.map(analyzePhase);
            return previousAnalyses.reduce(
                (acc, analysis) => {
                    const deltas = amount === "min" ? analysis.min : amount === "max" ? analysis.max : analysis.avg;
                    return {
                        cash: acc.cash + deltas.cash,
                        investments: acc.investments + deltas.investments,
                        happiness: acc.happiness + deltas.happiness,
                    };
                },
                { cash: 0, investments: 0, happiness: 0 },
            );
        },
        [phases, selectedPhaseId],
    );

    const handleApplyStats = useCallback(
        (amount: StatsAmount) => {
            const deltas = computeDeltas(amount);
            setFormData((prev) => ({
                ...prev,
                cashOverride: deltas.cash,
                investmentsOverride: deltas.investments,
                happinessOverride: deltas.happiness,
            }));
        },
        [computeDeltas],
    );

    const handleStart = useCallback(() => {
        if (!selectedPhaseId || !selectedPhase) return;

        saveFormData(formData);

        const store = useGameStore.getState();

        store.resetGame();

        if (!useMessageDelay) {
            store.setQueueTimerDuration(0);
        }

        store.addCash(cashOverride, "");
        store.addInvestments(investmentsOverride, "");
        store.setHappiness(happinessOverride);

        store.setFinanceTypeId(selectedFinanceTypeId);

        const messages = messageLimit > 0 ? selectedPhase.messages.slice(-messageLimit) : selectedPhase.messages;
        store.startPhase(selectedPhaseId, messages);

        store.selectRoom(roomVariant);

        onClose?.();
    }, [
        selectedPhaseId,
        selectedPhase,
        formData,
        useMessageDelay,
        cashOverride,
        investmentsOverride,
        happinessOverride,
        roomVariant,
        selectedFinanceTypeId,
        messageLimit,
        onClose,
    ]);

    const handleShowGameEnd = useCallback(() => {
        saveFormData(formData);

        const store = useGameStore.getState();

        store.setFinanceTypeId(selectedFinanceTypeId);
        store.setCurrentPhaseId(phases[phases.length - 1].documentId);
        store.selectRoom(roomVariant);
        store.finishGame();

        onClose?.();
    }, [onClose, selectedFinanceTypeId, phases, roomVariant, formData]);

    const toggleBtnClass = (active: boolean) =>
        clsx(
            "flex-1 rounded px-3 py-2 text-sm font-medium border cursor-pointer",
            active
                ? "bg-blue-500 text-white border-blue-500"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
        );

    return (
        <div className="w-full max-w-2xl mx-auto p-6 space-y-4 text-gray-900 overflow-y-auto max-h-screen">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Quick Play</h2>
                <button
                    className="rounded px-3 py-1 text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"
                    onClick={handleReset}
                >
                    Reset
                </button>
            </div>

            {/* Phase & Finanztyp selectors — side by side */}
            <div className="flex gap-4">
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Phase</label>
                    <select
                        className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm cursor-pointer"
                        value={selectedPhaseId ?? ""}
                        onChange={(e) => update("selectedPhaseId", e.target.value || null)}
                    >
                        {phases.map((phase) => (
                            <option key={phase.documentId} value={phase.documentId}>
                                {phase.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Finanztyp</label>
                    <select
                        className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm cursor-pointer"
                        value={selectedFinanceTypeId ?? ""}
                        onChange={(e) => update("selectedFinanceTypeId", e.target.value || null)}
                    >
                        <option value="">-- None --</option>
                        {financeTypes.map((ft) => (
                            <option key={ft.documentId} value={ft.documentId}>
                                {ft.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Room variant */}
            <div>
                <label className="block text-sm font-medium mb-1">Room Variant</label>
                <div className="flex gap-2">
                    {(["a", "b"] as const).map((v) => (
                        <button
                            key={v}
                            className={toggleBtnClass(roomVariant === v)}
                            onClick={() => update("roomVariant", v)}
                        >
                            Room {v.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats amount — action buttons that populate stat overrides */}
            <div>
                <label className="block text-sm font-medium mb-1">Apply Stats</label>
                <div className="flex gap-2">
                    {(["min", "avg", "max"] as const).map((s) => (
                        <button key={s} className={toggleBtnClass(false)} onClick={() => handleApplyStats(s)}>
                            {s.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stat overrides — labels above, inputs side by side */}
            <div className="flex gap-4">
                {(
                    [
                        ["Cash", "cashOverride", cashOverride],
                        ["Investments", "investmentsOverride", investmentsOverride],
                        ["Happiness", "happinessOverride", happinessOverride],
                    ] as Array<[string, keyof FormData, number]>
                ).map(([label, key, value]) => (
                    <div key={key} className="flex-1">
                        <label className="block text-sm font-medium mb-1">{label}</label>
                        <input
                            type="number"
                            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                            value={value}
                            onChange={(e) => update(key, Number(e.target.value) || 0)}
                        />
                    </div>
                ))}
            </div>

            {/* Message limit */}
            <div>
                <label className="block text-sm font-medium mb-1">Message Limit (0 = all)</label>
                <input
                    type="number"
                    className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={messageLimit}
                    min={0}
                    onChange={(e) => update("messageLimit", Number(e.target.value) || 0)}
                />
                {messageLimit > 0 && selectedPhase && (
                    <p className="text-xs text-gray-500 mt-1">
                        Will use last {Math.min(messageLimit, selectedPhase.messages.length)} of{" "}
                        {selectedPhase.messages.length} messages
                    </p>
                )}
            </div>

            {/* Message delay toggle */}
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                    Message Delay{" "}
                    {useMessageDelay ? (
                        <span className="text-green-700">enabled</span>
                    ) : (
                        <span className="text-red-500">disabled</span>
                    )}
                </label>
                <button
                    className={clsx(
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer",
                        useMessageDelay ? "bg-blue-500" : "bg-gray-300",
                    )}
                    onClick={() => update("useMessageDelay", !useMessageDelay)}
                >
                    <span
                        className={clsx(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            useMessageDelay ? "translate-x-6" : "translate-x-1",
                        )}
                    />
                </button>
            </div>

            {/* Actions */}
            <div className="space-y-2 pt-2">
                <button
                    className={clsx(
                        "block w-full rounded px-4 py-2 text-white font-medium",
                        selectedPhaseId
                            ? "bg-blue-500 hover:bg-blue-600 cursor-pointer"
                            : "bg-blue-300 cursor-not-allowed",
                    )}
                    disabled={!selectedPhaseId}
                    onClick={handleStart}
                >
                    Start Phase
                </button>
                <button
                    className="block w-full rounded bg-gray-700 px-4 py-2 text-white font-medium hover:bg-gray-800 cursor-pointer"
                    onClick={handleShowGameEnd}
                >
                    Show Game End Screen
                </button>
            </div>
        </div>
    );
};

export default QuickPlayTool;